import {
  deleteOpenFormDrafts,
  fetchAddressSearch,
  fetchBillChannels,
  fetchBillingAccounts,
  fetchHolidays,
  fetchOpenForm,
  fetchOpenFormAccount,
  fetchOpenFormDraft,
  fetchOpenFormDraftItemList,
  fetchOpenFormItemList,
  fetchOpenFormProductAvailability,
  fetchOpenFormSummary,
  parameterizedSearchContacts,
  postOpenForm,
  postOpenFormDraft,
} from '../../common/fetch.js';
import { type OpenFormAsync } from './OpenFormAsync.js';
import { type OpenFormIndicator } from './OpenFormComponents/OpenFormLoadingIndicator.js';
import type { OpenFormCustomerNeedRequest } from '../../generated/api/openFormCustomerNeedRequest.js';
import type { OpenFormDraftUpsert } from '../../generated/api/openFormDraftUpsert.js';
import type { SourceSystem } from '../../generated/api/sourceSystem.js';

export const OpenFormFetch = (async: typeof OpenFormAsync) => {
  class OpenFormPromise<Resolved> {
    #cache?: boolean;
    #indicator?: boolean;
    #key: string;

    #result?: (value: Resolved) => Resolved;
    #reject?: (reason: unknown) => void;
    #settle?: () => void;

    constructor(
      callback: (signal: AbortSignal) => Promise<Resolved>,
      readonly controller = new AbortController(),
      readonly promise = ({ disable, message }: Required<OpenFormIndicator>, signal?: AbortSignal) => {
        (this.#indicator = !!message) && async.actions.indicator({ disable, message, key: this.#key });
        return callback(signal ? AbortSignal.any([signal, this.controller.signal]) : this.controller.signal)
          .then(this.resolved().#result)
          .catch(this.rejected().#reject)
          .finally(this.settled().#settle);
      }
    ) {
      this.#key = String(callback);
    }

    get evict() {
      return () => async.cache.delete(this.#key);
    }

    cache(...key: (boolean | null | number | string)[]) {
      (this.#key += String(key.length && key)) && (this.#cache = true);
      return this;
    }

    execute(indicator: Required<OpenFormIndicator>, signal?: AbortSignal) {
      this.#cache
        ? async.cache.has(this.#key)
          ? async.cache.get(this.#key)!.then(this.#result)
          : async.cache.set(this.#key, this.promise(indicator, signal))
        : this.promise(indicator, signal);
      return this;
    }

    onReject({ reason }: PromiseRejectedResult, handler?: (reason: string) => void) {
      if (reason.name !== 'AbortError') {
        reason instanceof Response ? reason.text().then(handler).catch(handler) : handler?.(String(reason));
      }
      this.evict();
    }

    resolved(success?: (value: Resolved) => void) {
      this.#result ??= (value: Resolved) => {
        success?.(value);
        return value;
      };
      return this;
    }

    rejected(failure?: (reason: string) => void) {
      this.#reject ??= (reason: unknown) => {
        this.onReject({ reason } as PromiseRejectedResult, failure);
      };
      return this;
    }

    settled(finished?: () => void) {
      this.#settle ??= () => {
        finished?.();
        this.#indicator && async.actions.indicator({ key: this.#key });
      };
      return this;
    }
  }

  return {
    deleteOpenFormDrafts(query: { referenceNumbers: string[] }) {
      return new OpenFormPromise(signal => deleteOpenFormDrafts(query, { signal }));
    },
    getAddressSearch(query: { postalCode: string; query: string }) {
      return new OpenFormPromise(signal => fetchAddressSearch(query, { signal }));
    },
    getBillChannels(mdmId?: string) {
      return new OpenFormPromise(signal => fetchBillChannels(mdmId ?? async.accountMasterId, { signal }));
    },
    getBillingAccounts({ sourceSystem }: { sourceSystem: SourceSystem }) {
      return new OpenFormPromise(signal =>
        Promise.allSettled([
          fetchBillingAccounts({ sourceSystem }, { signal }),
          fetchBillingAccounts({ sourceSystem, useSearchService: true }, { signal }),
        ])
      );
    },
    getContacts(query: { searchTerm: string }) {
      return new OpenFormPromise(signal => parameterizedSearchContacts(query, { signal }));
    },
    getHolidays() {
      return new OpenFormPromise(signal => fetchHolidays({ signal }));
    },
    getOpenForm({ formId, referenceNumber }: OpenFormDraftUpsert, preview: boolean) {
      return new OpenFormPromise(signal =>
        Promise.all(
          !preview && async.flags.has('draft')
            ? referenceNumber
              ? [fetchOpenForm({ formId }, { signal }), fetchOpenFormDraft({ referenceNumber }, { signal })]
              : [fetchOpenForm({ formId }, { signal }), postOpenFormDraft({ formId }, { signal })]
            : [fetchOpenForm({ formId }, { signal })]
        )
      );
    },
    getOpenFormAccount() {
      return new OpenFormPromise(signal => fetchOpenFormAccount({ signal }));
    },
    getOpenFormListResponse() {
      return new OpenFormPromise(signal =>
        Promise.all(
          async.flags.add('draft') && [fetchOpenFormItemList({ signal }), fetchOpenFormDraftItemList({ signal })]
        )
      );
    },
    getOpenFormProductAvailability(query: { addressId: string; formId: string; subscriptionTypes: string[] }) {
      return new OpenFormPromise(signal => fetchOpenFormProductAvailability(query, { signal }));
    },
    getOpenFormSummary(query: { choices: string[] }) {
      return new OpenFormPromise(signal => fetchOpenFormSummary(query, { signal }));
    },
    postOpenForm(query: OpenFormCustomerNeedRequest) {
      return new OpenFormPromise(signal => postOpenForm(query, { signal }));
    },
    postOpenFormDraft(query: OpenFormDraftUpsert) {
      return new OpenFormPromise(signal => postOpenFormDraft(query, { signal }));
    },
  };
};
